import { PROVIDER_MAP } from '@/constants'
import BrandIcon from '@/sections/BrandIcon'
import i18n from '@/locales'
import { getServerConf } from '../utils'

/** 裸金属（baremetal）工单资源详情展示，字段参考 baremetal/sidepage/Detail */
export default {
  methods: {
    initBaremetalInfo () {
      const baremetalInfo = [
        {
          title: i18n.t('common_385'),
          items: [
            {
              field: 'name',
              title: i18n.t('common_186'),
              formatter: ({ row }) => {
                const serverConf = getServerConf(row)
                return serverConf.generate_name || serverConf.name || '-'
              },
            },
            {
              field: 'resource_type',
              title: i18n.t('common.resource_type'),
              formatter: () => {
                return i18n.te('dictionary.baremetal') ? i18n.t('dictionary.baremetal') : 'baremetal'
              },
            },
            {
              field: 'project',
              title: i18n.t('table.title.owner_project'),
              formatter: () => {
                const v = this.variables
                const domain = v.project_domain
                const project = v.project_name || v.project
                if (!project) return '-'
                return `${domain}/${project}`
              },
            },
            {
              field: 'region',
              title: i18n.t('common_282'),
              formatter: ({ row }) => this.formatRegionZone(row),
            },
            {
              field: 'hypervisor',
              title: i18n.t('common_283'),
              slots: {
                default: ({ row }) => {
                  const serverConf = getServerConf(row)
                  let brand = ''
                  if (serverConf.provider) {
                    brand = PROVIDER_MAP[serverConf.provider]?.brand || serverConf.provider
                  }
                  if (!brand) {
                    const formType = serverConf.extraData?.formType
                    if (formType === 'private') brand = 'Cloudpods'
                    else if (formType === 'onpremise' || formType === 'idc') brand = 'OneCloud'
                  }
                  if (!brand) return '-'
                  return [<BrandIcon name={brand} />]
                },
              },
            },
            {
              field: 'config',
              title: i18n.t('common_428'),
              formatter: ({ row }) => {
                const serverConf = getServerConf(row)
                if (serverConf.extraData?.specifications) {
                  return serverConf.extraData.specifications
                }
                const cpu = serverConf.vcpu_count
                const memory = serverConf.vmem_size
                if (!cpu && !memory) return '-'
                return (cpu || '-') + i18n.t('common_429') + ((memory / 1024) || '-') + 'GB'
              },
            },
            {
              field: 'count',
              title: i18n.t('common_430'),
              formatter: ({ row }) => {
                const serverConf = getServerConf(row)
                return (serverConf.count || serverConf.__count__ || 1) + i18n.t('common_431')
              },
            },
            {
              field: 'fake_create_from_bm_import',
              title: this.$t('compute.server_package.fake_create_from_bm_import'),
              formatter: ({ row }) => {
                const serverConf = getServerConf(row)
                const flag = serverConf.fake_create_from_bm_import ?? serverConf.extraData?.fake_create_from_bm_import
                const on = flag === true || flag === 'true' || flag === 1
                return on ? this.$t('table.title.on') : this.$t('table.title.off')
              },
            },
            {
              field: 'os_type',
              title: this.$t('compute.text_267'),
              formatter: ({ row }) => {
                const serverConf = getServerConf(row)
                if (this.isBaremetalFakeCreateFromBmImport(serverConf)) return '-'
                return serverConf.extraData?.os || row.imageInfo?.os_type || '-'
              },
            },
            {
              field: 'image',
              title: this.$t('compute.text_97'),
              formatter: ({ row }) => {
                const serverConf = getServerConf(row)
                if (this.isBaremetalFakeCreateFromBmImport(serverConf)) return '-'
                return row.imageInfo?.name || serverConf.extraData?.image || '-'
              },
            },
            {
              field: 'vcpu_count',
              title: 'CPU',
              formatter: ({ row }) => {
                const serverConf = getServerConf(row)
                if (!serverConf.vcpu_count) return '-'
                return serverConf.vcpu_count + this.$t('compute.text_167')
              },
            },
            {
              field: 'vmem_size',
              title: this.$t('compute.text_369'),
              formatter: ({ row }) => {
                const serverConf = getServerConf(row)
                if (!serverConf.vmem_size) return '-'
                return (serverConf.vmem_size / 1024) + 'GB'
              },
            },
            {
              field: 'sysDisk',
              title: this.$t('compute.text_49'),
              formatter: ({ row }) => {
                const serverConf = getServerConf(row)
                if (this.isBaremetalFakeCreateFromBmImport(serverConf)) return '-'
                return this.getBaremetalDiskInfo(serverConf).sysDisk || '-'
              },
            },
            {
              field: 'dataDisk',
              title: this.$t('compute.text_50'),
              formatter: ({ row }) => {
                const serverConf = getServerConf(row)
                if (this.isBaremetalFakeCreateFromBmImport(serverConf)) return '-'
                return this.getBaremetalDiskInfo(serverConf).dataDisk || '-'
              },
            },
            {
              field: 'user',
              title: this.$t('compute.text_1372'),
              formatter: ({ row }) => {
                const serverConf = getServerConf(row)
                if (this.isBaremetalFakeCreateFromBmImport(serverConf)) return '-'
                if (serverConf.keypair) {
                  return `root (${this.$t('compute.text_33')}: ${row.keypairInfo?.name ? `${row.keypairInfo.name}` : '-'})`
                }
                if (Object.prototype.hasOwnProperty.call(serverConf, 'reset_password') && !serverConf.reset_password) {
                  return `root (${this.$t('compute.text_164')}${this.$t('compute.text_34')})`
                }
                if (serverConf.password) {
                  return `root (${this.$t('compute.text_164')}${this.$t('compute.text_35')})`
                }
                return `root (${this.$t('compute.text_164')}${this.$t('compute.text_32')})`
              },
            },
            {
              field: 'cdrom',
              title: 'ISO',
              formatter: ({ row }) => {
                const serverConf = getServerConf(row)
                if (this.isBaremetalFakeCreateFromBmImport(serverConf)) return '-'
                const cdrom = serverConf.cdrom
                if (!cdrom) return '-'
                if (row.isoInfo && row.isoInfo.name) return row.isoInfo.name
                const idx = String(cdrom).indexOf('(')
                return idx > -1 ? cdrom.substring(0, idx) : cdrom
              },
            },
            {
              field: 'isolated_devices',
              title: 'GPU',
              formatter: ({ row }) => {
                const serverConf = getServerConf(row)
                if (!serverConf.isolated_devices) return '-'
                const gpuArr = serverConf.isolated_devices
                const obj = {}
                gpuArr.forEach(val => {
                  const model = val.model || `${val.vendor}/${val.model}`
                  obj[model] = (obj[model] || 0) + 1
                })
                return Object.keys(obj).map(k => this.$t('compute.text_370', [obj[k], k])).join('、') || '-'
              },
            },
            {
              field: 'network',
              title: i18n.t('compute.text_104'),
              slots: {
                default: ({ row }) => {
                  const serverConf = getServerConf(row)
                  if (this.isBaremetalFakeCreateFromBmImport(serverConf)) return '-'
                  return this.renderWorkflowNetworkInfo()
                },
              },
            },
            {
              field: 'schedtags',
              title: i18n.t('compute.text_311'),
              slots: {
                default: ({ row }) => {
                  const serverConf = getServerConf(row)
                  if (serverConf.prefer_host) {
                    return `${this.$t('compute.text_112')}: ${row.hostInfo?.name || serverConf.prefer_host}`
                  }
                  if (serverConf.schedtags && row.schedtagInfo) {
                    const ret = [<div>{this.$t('compute.text_3')}</div>]
                    ret.push(...row.schedtagInfo.map(item => {
                      return <div>{item.schedtagObj?.name}{item.strategy ? ` ${this.$te(`schedtagPolicys.${item.strategy}`) ? this.$t(`schedtagPolicys.${item.strategy}`) : item.strategy}` : ''}</div>
                    }))
                    return ret
                  }
                  return '-'
                },
              },
            },
            {
              field: 'description',
              title: this.$t('compute.text_312'),
              formatter: ({ row }) => getServerConf(row).description || '-',
            },
          ],
        },
      ]
      this.extraInfo.push(...baremetalInfo)
    },
    isBaremetalFakeCreateFromBmImport (serverConf = {}) {
      const flag = serverConf.fake_create_from_bm_import ?? serverConf.extraData?.fake_create_from_bm_import
      return flag === true || flag === 'true' || flag === 1
    },
    getBaremetalDiskInfo (serverConf) {
      const disks = serverConf.disks || []
      if (!disks.length) {
        return { sysDisk: '-', dataDisk: '-' }
      }
      const formatDisk = (disk) => {
        if (!disk) return '-'
        if (disk.size < 0) return '-'
        const sizeGb = parseInt(disk.size / 1024)
        return sizeGb > 0 ? `${sizeGb}GB` : '-'
      }
      const sysDisk = formatDisk(disks[0])
      if (disks.length === 1) {
        return { sysDisk, dataDisk: '-' }
      }
      const dataParts = disks.slice(1).map(formatDisk).filter(v => v && v !== '-')
      return {
        sysDisk,
        dataDisk: dataParts.length ? dataParts.join('、') : '-',
      }
    },
  },
}
